import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, Building2, Briefcase, FileText, ShieldCheck, Compass,
  TrendingUp, MonitorCheck, Filter, Workflow, ClipboardList, BarChart3,
  Sparkles, Users, Trophy, ArrowUpRight, Target, Award, BookOpen,
} from "lucide-react";
import { C, Reveal } from "./shared";

function StakeholderCard({ role, color, icon: Icon, items, footerIcon: FIcon, footerTitle, footerSubtitle, ctaTo, testid }) {
  return (
    <div className="group rounded-2xl overflow-hidden shadow-sm transition-all duration-500 flex flex-col h-full hover:-translate-y-2 hover:shadow-2xl"
      style={{ border: `1px solid ${C.rule}` }} data-testid={testid}>
      <div className="px-5 py-5 flex items-center gap-3 text-white relative overflow-hidden" style={{ background: color }}>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}b0 100%)` }} />
        <div className="relative w-11 h-11 rounded-full grid place-items-center transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6"
          style={{ background: "rgba(255,255,255,0.18)" }}>
          <Icon size={22} strokeWidth={2.2} />
        </div>
        <div className="relative leading-tight">
          <div className="font-display font-extrabold text-xs tracking-widest uppercase opacity-90">For</div>
          <div className="font-display font-black text-xl tracking-tight">{role}</div>
        </div>
      </div>
      <div className="p-5 space-y-3 flex-1 bg-white">
        {items.map(([IIcon, text]) => (
          <div key={text} className="flex items-start gap-3 transition-transform duration-300 hover:translate-x-0.5">
            <div className="w-8 h-8 rounded-md grid place-items-center shrink-0 mt-0.5 transition-all duration-300 hover:scale-110"
              style={{ background: `${color}15`, color }}>
              <IIcon size={15} strokeWidth={2.2} />
            </div>
            <p className="text-sm leading-snug" style={{ color: C.ink }}>{text}</p>
          </div>
        ))}
      </div>
      <Link to={ctaTo} className="px-5 py-4 flex items-center gap-3 text-white transition-all duration-300 hover:brightness-110 group/cta"
        style={{ background: color }} data-testid={`${testid}-cta`}>
        <FIcon size={22} className="transition-transform duration-300 group-hover/cta:scale-110 group-hover/cta:-rotate-6" />
        <div className="leading-tight flex-1">
          <div className="font-display font-black text-sm tracking-wide">{footerTitle}</div>
          <div className="text-xs opacity-90">{footerSubtitle}</div>
        </div>
        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-0.5" />
      </Link>
    </div>
  );
}

export default function StakeholdersSection() {
  return (
    <section id="stakeholders" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="font-semibold text-xs tracking-widest uppercase" style={{ color: C.green }}>One Platform</span>
          <h2 className="font-display font-black text-3xl md:text-5xl mt-2 tracking-tight">Four Stakeholders</h2>
          <p className="text-slate-600 mt-3">Purpose-built workspaces for students, working professionals, institutions and companies — the four pillars of the employability ecosystem.</p>
        </Reveal>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mt-12 scroll-mt-24">
          <Reveal delay={1} id="students">
            <StakeholderCard
              role="Students" color={C.green} icon={GraduationCap}
              testid="card-students"
              items={[
                [FileText, "Build a complete profile with education, skills, projects & resume"],
                [ShieldCheck, "Skill Evidence Locker for certificates & badges"],
                [Compass, "Personalized learning path recommendations"],
                [Briefcase, "Track applications and discover matched jobs & internships"],
                [TrendingUp, "Improve your employability score & stand out"],
              ]}
              footerIcon={Trophy}
              footerTitle="YOUR SKILLS."
              footerSubtitle="OUR PLATFORM. YOUR FUTURE."
              ctaTo="/signup?role=student"
            />
          </Reveal>
          <Reveal delay={2} id="professionals">
            <StakeholderCard
              role="Working Professionals" color={C.purple || "#6D28D9"} icon={Target}
              testid="card-professionals"
              items={[
                [TrendingUp, "Lateral roles tagged by years-of-experience & tech stack"],
                [Award, "Verified CoE certifications recruiters can trust"],
                [Sparkles, "AI resume scoring vs. real job postings"],
                [BookOpen, "Self-paced learning paths to switch / upskill"],
                [ShieldCheck, "Privacy-first — your employer never sees your profile"],
              ]}
              footerIcon={TrendingUp}
              footerTitle="YOUR NEXT MOVE."
              footerSubtitle="LATERAL ROLES. VERIFIED SKILLS. BETTER OFFERS."
              ctaTo="/signup?role=student&track=professional"
            />
          </Reveal>
          <Reveal delay={3} id="institutions">
            <StakeholderCard
              role="Institutions" color={C.navy} icon={Building2}
              testid="card-institutions"
              items={[
                [MonitorCheck, "Universities, colleges, polytechnics — one dashboard"],
                [Filter, "Bulk-import students, set eligibility & affiliation filters"],
                [Workflow, "Map programs, courses & batches to placement pathways"],
                [ClipboardList, "Run drives, issue verifiable certificates, track outcomes"],
                [BarChart3, "Outcome reports & NAAC / NBA accreditation exports"],
              ]}
              footerIcon={TrendingUp}
              footerTitle="BETTER INSIGHTS."
              footerSubtitle="BETTER PLACEMENTS. STRONGER CAMPUSES."
              ctaTo="/signup?role=institute"
            />
          </Reveal>
          <Reveal delay={4} id="companies">
            <StakeholderCard
              role="Companies" color={C.orange} icon={Briefcase}
              testid="card-companies"
              items={[
                [FileText, "Post jobs & internships in minutes"],
                [Filter, "Filter candidates by skills, course, year, location & verification"],
                [Sparkles, "Smart matching recommendations"],
                [Users, "Streamlined shortlisting & bulk actions"],
                [ClipboardList, "Integrated assessments (via partners)"],
              ]}
              footerIcon={Users}
              footerTitle="RIGHT TALENT."
              footerSubtitle="LESS TIME. BETTER HIRING."
              ctaTo="/signup?role=company"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
